import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  type QueryDocumentSnapshot,
  type DocumentData,
} from 'firebase/firestore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  User,
  Tag,
  Search,
  Filter,
  Loader2,
  Heart,
  GraduationCap,
  Briefcase,
  Accessibility,
  BookOpen,
  Monitor,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react';
import { useAccessibility } from '../hooks/useAccessibility';
import { ScrollReveal } from '../components/ScrollReveal';
import { cn } from '@/lib/utils';
import { blogDb, isBlogFirebaseConfigured } from '../lib/firebaseBlog';
import { getCachedListing, setCachedListing } from '../lib/blogCache';
import { BLOG_CATEGORY_OPTIONS, BLOG_CARD_COLOR_OPTIONS, type BlogPost } from '../dashboard/types/blog';

const PAGE_SIZE = 9;
const categorias = [{ id: 'todos', nome: 'Todos os Posts' }, ...BLOG_CATEGORY_OPTIONS];

const categoryIcons: Record<string, LucideIcon> = {
  pais: Heart,
  educadores: GraduationCap,
  profissionais: Briefcase,
  inclusao: Accessibility,
  educacao: BookOpen,
  recursos: Monitor,
  atualizacoes: RefreshCw,
};

async function fetchPage(cursor: QueryDocumentSnapshot<DocumentData> | null) {
  if (!blogDb) return { posts: [] as BlogPost[], lastDoc: null, done: true };
  const base = [where('status', '==', 'publicado'), orderBy('publicadoEm', 'desc'), limit(PAGE_SIZE)] as const;
  const q = cursor
    ? query(collection(blogDb, 'posts'), ...base, startAfter(cursor))
    : query(collection(blogDb, 'posts'), ...base);
  const snap = await getDocs(q);
  const posts = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as BlogPost);
  return { posts, lastDoc: snap.docs[snap.docs.length - 1] ?? null, done: snap.docs.length < PAGE_SIZE };
}

const Blog = () => {
  const { speak } = useAccessibility();
  const [filtroCategoria, setFiltroCategoria] = useState('todos');
  const [termoBusca, setTermoBusca] = useState('');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    const cached = getCachedListing();
    if (cached) {
      setPosts(cached.posts);
      setLastDoc(cached.lastDoc);
      setHasMore(!cached.done);
      setLoading(false);
      return;
    }
    (async () => {
      const { posts: fetched, lastDoc: cursor, done } = await fetchPage(null);
      setPosts(fetched);
      setLastDoc(cursor);
      setHasMore(!done);
      setCachedListing({ posts: fetched, lastDoc: cursor, done });
      setLoading(false);
    })();
  }, []);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    const { posts: more, lastDoc: cursor, done } = await fetchPage(lastDoc);
    const merged = [...posts, ...more];
    setPosts(merged);
    setLastDoc(cursor);
    setHasMore(!done);
    setCachedListing({ posts: merged, lastDoc: cursor, done });
    setLoadingMore(false);
  };

  const postsFiltrados = posts.filter((post) => {
    const matchCategoria = filtroCategoria === 'todos' || post.categoria === filtroCategoria;
    const termo = termoBusca.toLowerCase();
    const matchBusca =
      termoBusca === '' ||
      post.titulo.toLowerCase().includes(termo) ||
      post.resumo.toLowerCase().includes(termo);
    return matchCategoria && matchBusca;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-16 pb-14 md:pt-20 md:pb-16 bg-background overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.35] dark:opacity-[0.12]"
          style={{ backgroundImage: 'radial-gradient(rgb(var(--primary)) 1px, transparent 1px)', backgroundSize: '26px 26px' }}
          aria-hidden="true"
        />
        <div
          className="absolute -top-24 -left-24 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgb(var(--primary)) 0%, transparent 70%)' }}
          aria-hidden="true"
        />
        <div
          className="absolute -bottom-16 -right-16 w-80 h-80 rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgb(var(--warm)) 0%, transparent 70%)' }}
          aria-hidden="true"
        />

        <div className="relative container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-primary/10 dark:bg-primary/15 px-3 py-1.5 rounded-full text-sm font-semibold text-foreground mb-6">
              <Tag className="w-4 h-4 text-primary-ink dark:text-primary-tint" />
              Blog CONECTAR
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-[1.1]">
              Conhecimento que <span className="text-primary">transforma</span>
            </h1>

            <p className="font-body text-lg md:text-xl text-muted-foreground leading-relaxed">
              Artigos, bastidores da pesquisa e novidades sobre o desenvolvimento
              do CONECTAR.
            </p>
          </div>
        </div>
      </section>

      {/* Filtros e Busca */}
      <ScrollReveal>
        <section className="py-8 bg-primary/10">
          <div className="container mx-auto px-4">
            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <input
                  type="text"
                  placeholder="Buscar artigos..."
                  value={termoBusca}
                  onChange={(e) => setTermoBusca(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <select
                  value={filtroCategoria}
                  onChange={(e) => setFiltroCategoria(e.target.value)}
                  className="border border-border rounded-lg px-3 py-2 bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {categorias.map((categoria) => (
                    <option key={categoria.id} value={categoria.id}>
                      {categoria.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Posts */}
      <ScrollReveal>
        <section className="pt-12 pb-24 bg-primary/10">
          <div className="container mx-auto px-4">
            {!isBlogFirebaseConfigured() ? (
              <div className="text-center py-12">
                <p className="text-xl text-muted-foreground">O blog ainda está sendo configurado. Volte em breve.</p>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin" /> Carregando artigos…
              </div>
            ) : postsFiltrados.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-xl text-muted-foreground">
                  Nenhum artigo encontrado com os filtros selecionados.
                </p>
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
                  {postsFiltrados.map((post) => {
                    const CategoryIcon = post.categoria ? categoryIcons[post.categoria] : undefined;
                    const cardColorClass =
                      BLOG_CARD_COLOR_OPTIONS.find((c) => c.id === post.corCard)?.className ?? 'bg-primary';
                    return (
                      <div key={post.id}>
                        {/* Autor + data, fora do card */}
                        <div className="flex items-center gap-2.5 mb-3">
                          {post.autorFotoUrl ? (
                            <img src={post.autorFotoUrl} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                              <User className="h-3.5 w-3.5 text-primary" />
                            </div>
                          )}
                          <span className="text-sm font-semibold text-foreground truncate">{post.autorNome}</span>
                          {post.publicadoEm && (
                            <span className="text-xs text-muted-foreground uppercase ml-auto shrink-0">
                              {new Date(post.publicadoEm)
                                .toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
                                .replace('.', '')}
                            </span>
                          )}
                        </div>

                        <article className="rounded-[1.75rem] shadow-md overflow-hidden hover:shadow-xl transition-shadow group">
                          <Link to={`/blog/${post.slug}`} onClick={() => speak(`Ler artigo: ${post.titulo}`)}>
                            {post.capa ? (
                              <img
                                src={post.capa}
                                alt={post.titulo}
                                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            ) : (
                              <div className={cn('w-full h-48 opacity-20', cardColorClass)} />
                            )}

                            <div className={cn('p-5', cardColorClass)}>
                              {post.categoria && (
                                <div className="flex items-center gap-1.5 text-xs font-medium text-primary-foreground/70 mb-2">
                                  {CategoryIcon && <CategoryIcon className="h-3.5 w-3.5" />}
                                  {categorias.find((c) => c.id === post.categoria)?.nome}
                                </div>
                              )}
                              <h2 className="font-semibold text-primary-foreground text-lg leading-snug line-clamp-2">
                                {post.titulo}
                              </h2>
                            </div>
                          </Link>
                        </article>
                      </div>
                    );
                  })}
                </div>

                {hasMore && (
                  <div className="flex justify-center mt-12">
                    <button
                      type="button"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="inline-flex items-center gap-2 bg-primary/5 hover:bg-primary/10 text-primary font-medium px-6 py-3 rounded-lg transition-colors disabled:opacity-60"
                    >
                      {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                      Carregar mais
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </ScrollReveal>

      <Footer />
    </div>
  );
};

export default Blog;
